import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, LogIn, Shield } from 'lucide-react';
import { adminLogin } from '../../utils/adminApi';
import { useAdmin } from '../../contexts/AdminContext';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAdmin();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await adminLogin(credentials.username, credentials.password);
      login();
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0c0f] via-[#141619] to-[#1a1d23] flex items-center justify-center circuit-pattern">
      {/* Background decorative elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-[100px] animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#5A6F8F]/10 rounded-full blur-[100px] animate-pulse animation-delay-2000"></div>

      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] rounded-2xl mb-4 shadow-lg">
            <Shield className="w-10 h-10 text-[#141619]" />
          </div>
          <h1 className="text-3xl font-bold text-gold-metallic mb-2">Admin Panel</h1>
          <p className="text-gray-400">PrimeDigital Management</p>
        </div>

        {/* Login Card */}
        <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] shadow-2xl p-8 relative overflow-hidden">
          {/* Decorative corner */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <LogIn className="w-6 h-6 text-[#D4AF37]" />
              <span>Sign In</span>
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                  <Input
                    type="text"
                    name="username"
                    value={credentials.username}
                    onChange={handleChange}
                    required
                    placeholder="Enter your username"
                    className="w-full pl-12 pr-4 py-3 bg-[#141619] border-[#2a3441] text-white rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <Lock className="w-5 h-5 text-gray-400" />
                  </div>
                  <Input
                    type="password"
                    name="password"
                    value={credentials.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter your password"
                    className="w-full pl-12 pr-4 py-3 bg-[#141619] border-[#2a3441] text-white rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] rounded-full font-bold text-sm uppercase tracking-widest hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 btn-premium"
              >
                {loading ? (
                  <span>Signing in...</span>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Back to Site Link */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-[#D4AF37] transition-colors duration-300 text-sm"
          >
            ← Back to Website
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
